import { useMemo, useRef, useState, type FormEvent } from 'react'
import { ObsidiaFrame, type ObsidiaState } from './ObsidiaFrame'
import { useObsidia, type Episode } from './useObsidia'

/**
 * obsidia.work.
 *
 * Canon calls this page "bookmark, status, dry chat, talking-head frame", and
 * uses the URL itself as a prop on screen — a browser tab reading obsidia.work.
 * So it is a single page that looks like somewhere she lives, not a product
 * landing page with a chat widget bolted on.
 */

// TODO(canon): source from docs/episodes at build time once replay ships.
const EPISODES: Episode[] = [
  {
    id: 's01e13',
    title: 'Errata',
    synopsis:
      'A printing error ships. The studio argues about whether to eat the reprint cost or issue a correction sheet, and about who signed off.',
  },
]

export default function App() {
  const {
    turns, streaming, error, paywalled, remaining, teaserTurns,
    send, stop, speaking, muted, toggleMute, primeAudio, blocked, replay, voiceError,
  } = useObsidia()
  const [draft, setDraft] = useState('')
  const [episodeId, setEpisodeId] = useState<string | null>(null)
  const logRef = useRef<HTMLDivElement>(null)

  const episode = useMemo(() => EPISODES.find((e) => e.id === episodeId), [episodeId])
  const state: ObsidiaState =
    streaming || speaking ? 'speaking' : draft.length > 0 ? 'listening' : 'idle'

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    // Unlock audio HERE, inside the click/submit gesture. By the time the reply
    // has streamed and been synthesized we are two awaits away and some
    // browsers no longer count it as user-initiated.
    if (!muted) primeAudio()
    const text = draft
    setDraft('')
    void send(text, episode).then(() => {
      logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' })
    })
  }

  return (
    <main className="shell">
      <section className="feed">
        <div className="frame">
          <ObsidiaFrame state={state} />
          <div className="chrome">
            <span className="dot" data-live={streaming || undefined} />
            <span className="host">obsidia.work</span>
            <span className="status" aria-live="polite">
              {speaking ? 'speaking' : streaming ? 'typing' : state === 'listening' ? 'listening' : 'standing by'}
            </span>
          </div>
        </div>

        <div className="modes">
          <button type="button" className={!episodeId ? 'on' : ''} onClick={() => setEpisodeId(null)}>
            Live
          </button>
          {EPISODES.map((ep) => (
            <button
              key={ep.id}
              type="button"
              className={episodeId === ep.id ? 'on' : ''}
              onClick={() => setEpisodeId(ep.id)}
            >
              Replay · {ep.title}
            </button>
          ))}
          {/* Muting also skips the request, so it costs nothing to leave off. */}
          <button type="button" className="mute" onClick={toggleMute} aria-pressed={muted}>
            {muted ? 'voice off' : 'voice on'}
          </button>
        </div>
      </section>

      <section className="talk">
        <header>
          <h1>Obsidia</h1>
          <p className="sub">
            {episode
              ? `Going back through “${episode.title}”. She was there.`
              : 'She stayed in the wiring. Ask her something.'}
          </p>
        </header>

        <div className="log" ref={logRef}>
          {turns.length === 0 && !paywalled && (
            <p className="empty">
              She is not a support bot and will tell you so. Ask about the studio, the industry, or what
              shipping a book actually costs.
            </p>
          )}

          {turns.map((t, i) => (
            <article key={i} className={t.role}>
              <span className="who">{t.role === 'assistant' ? 'Obsidia' : 'You'}</span>
              <p>
                {t.content}
                {streaming && i === turns.length - 1 && t.role === 'assistant' && <span className="caret" />}
              </p>
            </article>
          ))}

          {error && (
            <p role="alert" className="err">
              {error}
            </p>
          )}

          {/* Without this the hook could set voiceError and the page would
              still look like nothing happened — the exact reason this stayed
              undiagnosable for three deploys. */}
          {voiceError && !blocked && (
            <p className="err">
              {voiceError}{' '}
              <button type="button" className="linky" onClick={replay}>
                retry
              </button>
            </p>
          )}

          {blocked && (
            <p className="err">
              Your browser blocked autoplay.{' '}
              <button type="button" className="linky" onClick={replay}>
                play her reply
              </button>
            </p>
          )}

          {paywalled && (
            <div className="paywall">
              <p className="line">“That is as far as the free ones go.”</p>
              <p className="body">
                Live conversation with Obsidia is for monthly subscribers. {teaserTurns} turns are on the house
                so you know what you are paying for.
              </p>
              {/* TODO(auth): wire to the Atlas checkout + session once the
                  obsidia.feat.chat SKU is provisioned. */}
              <button type="button" className="cta" disabled title="Not yet wired">
                Subscribe
              </button>
            </div>
          )}
        </div>

        <form onSubmit={onSubmit}>
          <label className="sr-only" htmlFor="say">
            Say something to Obsidia
          </label>
          <input
            id="say"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={paywalled ? 'Subscribers only from here.' : 'Say something…'}
            disabled={streaming || paywalled}
            autoComplete="off"
          />
          {streaming ? (
            <button type="button" onClick={stop} className="ghost">
              Stop
            </button>
          ) : (
            <button type="submit" disabled={!draft.trim() || paywalled}>
              Send
            </button>
          )}
        </form>

        <footer>
          {!paywalled && remaining > 0 && (
            <span className="turns">
              {remaining} free {remaining === 1 ? 'turn' : 'turns'} left
            </span>
          )}
          <span className="fine">
            Obsidia is a character from a television show, running on a language model. She is not a person.
          </span>
        </footer>
      </section>
    </main>
  )
}
